<template>
  <div>
    <SideBar :sidebar-open="sidebarOpen" @update:sidebar-open="(value) => (sidebarOpen = value)" />

    <div class="xl:pl-72">
      <div
        class="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-6 border-b border-white/5 bg-background px-4 shadow-sm sm:px-6 lg:px-8"
      >
        <button type="button" id="sidebar" class="-m-2.5 p-2.5 text-white xl:hidden" @click="sidebarOpen = true">
          <span class="sr-only">{{ t("common.openSidebar") }}</span>
          <Bars3Icon class="h-5 w-5" aria-hidden="true" />
        </button>

        <SearchBar :model-value="search" @update:model-value="(text) => (search = text)" />
      </div>

      <AdminHeader />

      <main class="flex flex-col space-y-6 px-12 py-10">
        <template v-if="!isLicensed">
          <div
            class="relative mb-4 w-full rounded-lg border border-destructive/50 p-4 text-destructive dark:border-destructive [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-destructive [&>svg~*]:pl-7"
            role="alert"
          >
            <ExclamationTriangleIcon class="h-4 w-4" />
            <h5 class="mb-1 font-medium leading-none tracking-tight">
              {{ t("err.youDoNotHaveALicense") }}
            </h5>
            <i18n-t class="text-sm [&_p]:leading-relaxed" keypath="err.unlicensedDescription" tag="p">
              <template v-slot:url>
                <a href="https://licensing.knocknoc.io" class="underline hover:no-underline" target="_blank">
                  licensing.knocknoc.io
                </a>
              </template>
            </i18n-t>
          </div>
        </template>
        <template v-else>
          <div class="flex w-full justify-center">
            <KnocStepper
              v-model="wizard.step"
              :steps="[
                {
                  title: t('pages.wizard.stepper.settings.title'),
                  description: t('pages.wizard.stepper.settings.description'),
                  icon: Cog8ToothIcon,
                  step: 1,
                },
                {
                  title: t('pages.wizard.stepper.knocoptions.title'),
                  description: t('pages.wizard.stepper.knocoptions.description'),
                  icon: ServerIcon,
                  step: 2,
                },
                {
                  title: t('pages.wizard.stepper.apikey.title'),
                  description: t('pages.wizard.stepper.apikey.description'),
                  icon: KeyIcon,
                  step: 3.1,
                  disabled: wizard.form?.ApplicationType === 'active',
                },
                {
                  title: t('pages.wizard.stepper.agent.title'),
                  description: t('pages.wizard.stepper.agent.description'),
                  icon: CommandLineIcon,
                  step: 3.2,
                  disabled: wizard.form?.ApplicationType === 'passive',
                },
                {
                  title: t('pages.wizard.stepper.users.title'),
                  description: t('pages.wizard.stepper.users.description'),
                  icon: UsersIcon,
                  step: 4,
                },
                {
                  title: t('pages.wizard.stepper.review.title'),
                  description: t('pages.wizard.stepper.review.description'),
                  icon: ClipboardIcon,
                  step: 5,
                },
              ]"
            />
          </div>

          <template v-if="wizard.step === 1">
            <KnocForm
              v-model="wizard.form"
              :knoc-options-form="wizard.knocOptionsForm"
              type="APPLICATION"
              :agent="selectedAgent"
              @submit="wizard.changeStep(2)"
              @submit-invalid="globals.focusFirstError"
            >
              <button
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-muted-foreground underline underline-offset-4 ring-offset-background transition-colors hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                type="button"
                @click="router.back()"
              >
                {{ t("common.back") }}
              </button>

              <button
                data-testid="next-button"
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                type="submit"
              >
                {{ t("common.next") }}
              </button>
            </KnocForm>
          </template>

          <template v-if="wizard.step === 2">
            <KnocOptionsForm
              v-model="wizard.knocOptionsForm"
              :form="wizard.form"
              type="APPLICATION"
              @submit="
                wizard.changeStep(
                  wizard.form?.ApplicationType === 'passive' || wizard.form?.ApplicationType === 'passive+' ? 3.1 : 3.2,
                )
              "
              @submit-invalid="globals.focusFirstError"
            >
              <button
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-muted-foreground underline underline-offset-4 ring-offset-background transition-colors hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                type="button"
                @click="wizard.changeStep(1)"
              >
                {{ t("common.back") }}
              </button>

              <button
                data-testid="next-button"
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                type="submit"
              >
                {{ t("common.next") }}
              </button>
            </KnocOptionsForm>
          </template>

          <template v-if="wizard.step === 3.1">
            <div class="flex flex-col space-y-6">
              <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
                {{ t("pages.wizard.steps.apiKey.heading") }}
              </h3>

              <div class="w-full">
                <FormKit
                  v-model="wizard.apiKeyOption"
                  :classes="{
                    outer: 'w-full',
                  }"
                  :options="
                    apiKeyEntryOptions.map((option) => ({
                      label: t(`pages.wizard.steps.apiKey.entryOptions.${option}`),
                      value: option,
                    }))
                  "
                  :type="toggleButtons"
                />
              </div>

              <FormKit
                v-if="wizard.apiKeyOption === 'new'"
                v-model="wizard.apiKeyForm"
                type="form"
                :actions="false"
                @submit="wizard.changeStep(wizard.form?.ApplicationType === 'passive+' ? 3.2 : 4)"
                @submit-invalid="globals.focusFirstError"
              >
                <div class="flex flex-col space-y-4">
                  <div>
                    <FormKit
                      name="Name"
                      :label="t('pages.wizard.steps.apiKey.fields.Name.label')"
                      :placeholder="t('pages.wizard.steps.apiKey.fields.Name.placeholder')"
                      type="text"
                      validation="required"
                      :autocomplete="autoComplete"
                    />

                    <label
                      class="formkit-label mb-1 inline-flex text-sm font-bold text-neutral-700 dark:text-neutral-300"
                      for="AllowedFrom"
                    >
                      {{ t("pages.wizard.steps.apiKey.fields.AllowedFrom.label") }}
                    </label>
                    <FormKit type="list" name="AllowedFrom" dynamic #default="{ items, node, value }">
                      <div class="flex max-w-[712px] flex-col">
                        <FormKit type="group" v-for="(item, index) in items" :key="item" :index="index">
                          <div class="flex space-x-4">
                            <FormKit
                              type="text"
                              name="prefix"
                              :label="t('pages.wizard.steps.apiKey.fields.AllowedFrom.fields.prefix.label')"
                              :placeholder="t('pages.wizard.steps.apiKey.fields.AllowedFrom.fields.prefix.placeholder')"
                              validation="required"
                              :autocomplete="autoComplete"
                            />

                            <FormKit
                              type="text"
                              name="note"
                              :label="t('pages.wizard.steps.apiKey.fields.AllowedFrom.fields.note.label')"
                              :placeholder="
                                t(
                                  `pages.wizard.steps.apiKey.fields.AllowedFrom.fields.note.placeholders.${Math.floor(Math.random() * 5)}`,
                                )
                              "
                              validation="required"
                              :autocomplete="autoComplete"
                            />

                            <button
                              class="mt-6 inline-flex h-10 w-10 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                              type="button"
                              :aria-label="t('common.delete')"
                              @click="() => node.input(value.filter((_, i) => i !== index))"
                            >
                              <TrashIcon class="h-4 w-4" />
                            </button>
                          </div>
                        </FormKit>

                        <button
                          class="mt-2 inline-flex h-9 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                          type="button"
                          @click="() => node.input(value.concat({}))"
                        >
                          {{ t("pages.wizard.steps.apiKey.fields.AllowedFrom.add") }}
                        </button>
                      </div>
                    </FormKit>
                  </div>

                  <div class="flex justify-end space-x-4">
                    <button
                      class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-muted-foreground underline underline-offset-4 ring-offset-background transition-colors hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                      type="button"
                      @click="wizard.changeStep(2)"
                    >
                      {{ t("common.back") }}
                    </button>

                    <button
                      data-testid="next-button"
                      class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                      type="submit"
                    >
                      {{ t("common.next") }}
                    </button>
                  </div>
                </div>
              </FormKit>

              <div v-if="wizard.apiKeyOption === 'existing'">
                <AutoTable :columns="apiKeyTableCols" :rows="apiKeyTableRows">
                  <template #default>
                    <TableRow>
                      <TableCell :colspan="apiKeyTableCols?.length" class="text-center text-muted-foreground">
                        <div class="flex items-center justify-center gap-2">
                          <InformationCircleIcon class="h-4 w-4" aria-hidden="true" />
                          <span> {{ t("pages.wizard.steps.apiKey.none") }} </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  </template>
                  <template #col-Check="{ data }">
                    <span
                      :class="{
                        'formkit-decorator relative block aspect-[1/1] w-[1em] select-none rounded-full border border-neutral-400 bg-white text-lg text-transparent shadow ring-blue-500 group-[]/multistep:shadow-none group-[]/repeater:shadow-none peer-focus-visible:ring-2 peer-focus-visible:ring-offset-1 dark:border-neutral-500 dark:bg-transparent dark:ring-offset-blue-500 dark:peer-focus-visible:ring-1': true,
                        '!bg-blue-50 !text-blue-600 dark:!border-blue-600 dark:!bg-blue-900 dark:!text-blue-400':
                          wizard.existingApiKey?.ID === data.ID,
                      }"
                    />
                  </template>
                </AutoTable>
                <div class="mt-10 flex justify-end space-x-4">
                  <button
                    class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-muted-foreground underline underline-offset-4 ring-offset-background transition-colors hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                    type="button"
                    @click="wizard.changeStep(2)"
                  >
                    {{ t("common.back") }}
                  </button>

                  <button
                    data-testid="next-button"
                    class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                    type="button"
                    @click="
                      wizard.existingApiKey?.ID &&
                      wizard.changeStep(wizard.form?.ApplicationType === 'passive+' ? 3.2 : 4)
                    "
                  >
                    {{ t("common.next") }}
                  </button>
                </div>
              </div>

              <div v-if="wizard.apiKeyOption === 'none'" class="flex flex-col space-y-6">
                <div class="mb-4 rounded-lg border-2 border-amber-300/40 p-4 shadow-md">
                  <div class="flex items-start space-x-3">
                    <div class="mb-2 text-lg font-semibold text-amber-300">
                      <ExclamationTriangleIcon class="mt-1 h-6 w-6" />
                    </div>

                    <div class="flex-1">
                      <h4 class="mb-2 text-lg font-semibold text-amber-300">Security Warning</h4>
                      <div class="text-base leading-relaxed text-amber-400">
                        <p class="mb-3">
                          Selecting this option means no username/password is required to access the allow-list. This
                          could result in the disclosure of end-user IP addresses who are accessing the Knocknoc system.
                        </p>
                        <p class="mb-3">
                          The URI to the allow-list does contain random characters, however it should not be relied on
                          for security.
                        </p>
                        <p>Unfortunately this option exists as some vendors only support public IP address lists.</p>
                      </div>
                    </div>
                  </div>
                </div>

                <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
                  {{ t("Allowlist access IP restriction") }}
                </h3>

                <FormKit
                  v-model="wizard.allowlistAccessIpRestrictionForm"
                  type="form"
                  :actions="false"
                  :validation-rules="{
                    entryRequired: () => wizard.allowlistAccessIpRestrictionForm.AllowedFrom.length > 0,
                  }"
                  validation="entryRequired"
                  :validation-messages="{ entryRequired: 'At least one IP range must be present' }"
                  :key="wizard.allowlistAccessIpRestrictionForm.AllowedFrom.length"
                  @submit="wizard.changeStep(wizard.form?.ApplicationType === 'passive+' ? 3.2 : 4)"
                  @submit-invalid="globals.focusFirstError"
                >
                  <div class="flex flex-col space-y-4">
                    <div>
                      <label
                        class="formkit-label mb-1 inline-flex text-sm font-bold text-neutral-700 dark:text-neutral-300"
                        for="AllowedFrom"
                      >
                        {{ t("pages.wizard.steps.apiKey.fields.AllowedFrom.label") }}
                      </label>
                      <FormKit type="list" name="AllowedFrom" dynamic #default="{ items, node, value }">
                        <div class="flex max-w-[712px] flex-col">
                          <FormKit type="group" v-for="(item, index) in items" :key="item" :index="index">
                            <div class="flex space-x-4">
                              <FormKit
                                type="text"
                                name="prefix"
                                :label="t('pages.wizard.steps.apiKey.fields.AllowedFrom.fields.prefix.label')"
                                :placeholder="
                                  t('pages.wizard.steps.apiKey.fields.AllowedFrom.fields.prefix.placeholder')
                                "
                                validation="required"
                                :autocomplete="autoComplete"
                              />

                              <FormKit
                                type="text"
                                name="note"
                                :label="t('pages.wizard.steps.apiKey.fields.AllowedFrom.fields.note.label')"
                                :placeholder="
                                  t(
                                    `pages.wizard.steps.apiKey.fields.AllowedFrom.fields.note.placeholders.${Math.floor(Math.random() * 5)}`,
                                  )
                                "
                                validation="required"
                                :autocomplete="autoComplete"
                              />

                              <button
                                class="mt-6 inline-flex h-10 w-10 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                                type="button"
                                :aria-label="t('common.delete')"
                                @click="() => node.input(value.filter((_, i) => i !== index))"
                              >
                                <TrashIcon class="h-4 w-4" />
                              </button>
                            </div>
                          </FormKit>

                          <button
                            class="mt-2 inline-flex h-9 w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-input bg-background px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                            type="button"
                            @click="() => node.input(value.concat({}))"
                          >
                            {{ t("pages.wizard.steps.apiKey.fields.AllowedFrom.add") }}
                          </button>
                        </div>
                      </FormKit>
                    </div>

                    <div class="flex justify-end space-x-4">
                      <button
                        class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-muted-foreground underline underline-offset-4 ring-offset-background transition-colors hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                        type="button"
                        @click="wizard.changeStep(2)"
                      >
                        {{ t("common.back") }}
                      </button>

                      <button
                        data-testid="next-button"
                        class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                        type="submit"
                      >
                        {{ t("common.next") }}
                      </button>
                    </div>
                  </div>
                </FormKit>
              </div>
            </div>
          </template>

          <template v-if="wizard.step === 3.2">
            <AgentSelect
              :options="agents"
              :is-capable="isAgentCapable"
              :is-version-capable="isAgentCapable"
              :value="wizard.agentId"
              :onSelect="wizard.selectAgent"
            />

            <div class="flex justify-end space-x-4">
              <button
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-muted-foreground underline underline-offset-4 ring-offset-background transition-colors hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                type="button"
                @click="wizard.changeStep(wizard.form?.ApplicationType === 'active' ? 2 : 3.1)"
              >
                {{ t("common.back") }}
              </button>

              <button
                data-testid="next-button"
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                @click="wizard.changeStep(4)"
                :disabled="!wizard.agentId"
              >
                {{ t("common.next") }}
              </button>
            </div>
          </template>

          <template v-if="wizard.step === 4">
            <GroupSelect :onSelect="wizard.selectGroup" :options="groups" :value="wizard.groupIds" />
            <UserSelect
              :options="users.filter((user) => user.AuthType !== 'SAML')"
              :value="wizard.userIds"
              :onSelect="wizard.selectUser"
              :onCreate="handleCreateUser"
            />

            <div class="flex justify-end space-x-4">
              <button
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-muted-foreground underline underline-offset-4 ring-offset-background transition-colors hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                type="button"
                @click="wizard.changeStep(wizard.form?.ApplicationType === 'passive' ? 3.1 : 3.2)"
              >
                {{ t("common.back") }}
              </button>

              <button
                data-testid="next-button"
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                @click="wizard.changeStep(5)"
              >
                {{ t("common.next") }}
              </button>
            </div>
          </template>

          <template v-if="wizard.step === 5">
            <div class="grid grid-cols-2 gap-6">
              <div class="flex flex-col space-y-6">
                <div
                  data-testid="settings-card"
                  class="flex flex-col space-y-6 rounded-lg border bg-background p-6 shadow-sm transition-colors"
                >
                  <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
                    {{ t("pages.wizard.steps.settings.heading") }}
                  </h3>

                  <div class="grid w-full grid-cols-2 gap-4">
                    <div>
                      <h4 class="text-sm font-medium text-muted-foreground">
                        {{ t("pages.wizard.steps.settings.fields.ApplicationType.label") }}
                      </h4>
                      <p class="truncate">
                        {{
                          t(`pages.wizard.steps.settings.fields.ApplicationType.options.${wizard.form.ApplicationType}`)
                        }}
                      </p>
                    </div>

                    <template v-if="wizard.form.ApplicationType === 'passive+'">
                      <div>
                        <h4 class="text-sm font-medium text-muted-foreground">
                          {{ t("pages.wizard.steps.settings.fields.Vendor.passive+.label") }}
                        </h4>
                        <p class="truncate">
                          {{ t(`pages.wizard.steps.settings.fields.Vendor.passive+.options.${wizard.form.Vendor}`) }}
                        </p>
                      </div>
                      <template v-if="wizard.form.Vendor === 'palo'">
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t("pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.Hostname.label")
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.panos.Hostname }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t("pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.Insecure.label")
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.panos.Insecure ? "yes" : "no" }}
                          </p>
                        </div>
                        <div class="col-span-2">
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{ t("pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.ApiKey.label") }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.panos.ApiKey ? "provided" : "-" }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{ t("pages.wizard.steps.settings.fields.ACLConfig.fields.panos.fields.EDLName.label") }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.ACLConfig.panos.EDLName || "-" }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.VSys.summaryLabel",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.panos.VSys || "-" }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t("pages.wizard.steps.settings.fields.ACLConfig.fields.panos.fields.SerialNumber.label")
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.ACLConfig.panos.SerialNumbers?.join(", ") || "-" }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t("pages.wizard.steps.settings.fields.ACLConfig.fields.panos.fields.MapUser.summaryLabel")
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.panos.MapUser ? "yes" : "no" }}
                          </p>
                        </div>
                        <div v-if="wizard.form.BackendConfig.panos.MapUserAlternateHost">
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.Hostname.altLabel",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.panos.MapUserHostname || "-" }}
                          </p>
                        </div>
                        <div v-if="wizard.form.BackendConfig.panos.MapUserAlternateHost">
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t("pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.ApiKey.altLabel")
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.panos.MapUserApiKey ? "provided" : "-" }}
                          </p>
                        </div>
                        <div v-if="wizard.form.BackendConfig.panos.MapUserAlternateHost">
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.VSys.altSummaryLabel",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.panos.MapUserVSys || "-" }}
                          </p>
                        </div>
                        <div v-if="wizard.form.BackendConfig.panos.MapUserAlternateHost">
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.SerialNumber.altLabel",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.panos.MapUserSerialNumbers?.join(", ") || "-" }}
                          </p>
                        </div>
                      </template>
                      <template v-else-if="wizard.form.Vendor === 'sonicwall'">
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.sonicwall.fields.Hostname.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.sonicwall.Hostname }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.sonicwall.fields.Insecure.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.sonicwall.Insecure ? "true" : "false" }}
                          </p>
                        </div>
                        <div class="col-span-2">
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.sonicwall.fields.Username.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.sonicwall.Username }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.ACLConfig.fields.sonicwall.fields.DynamicGroup.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.ACLConfig.sonicwall.DynamicGroup }}
                          </p>
                        </div>
                      </template>
                    </template>
                    <template v-if="wizard.form.ApplicationType === 'active'">
                      <div>
                        <h4 class="text-sm font-medium text-muted-foreground">
                          {{ t("pages.wizard.steps.settings.fields.Vendor.active.label") }}
                        </h4>
                        <p class="truncate">
                          {{ t(`pages.wizard.steps.settings.fields.Vendor.active.options.${wizard.form.Vendor}`) }}
                        </p>
                      </div>
                      <template v-if="wizard.form.Vendor === 'fortinet'">
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{ t("pages.wizard.steps.settings.fields.BackendConfig.fields.fortios.fields.Url.label") }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.fortios.Url }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t("pages.wizard.steps.settings.fields.BackendConfig.fields.fortios.fields.Insecure.label")
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.fortios.Insecure ? "true" : "false" }}
                          </p>
                        </div>
                        <div class="col-span-2">
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t("pages.wizard.steps.settings.fields.BackendConfig.fields.fortios.fields.Bearer.label")
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.fortios.Bearer }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{ t("pages.wizard.steps.settings.fields.ACLConfig.fields.fortios.fields.Name.label") }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.ACLConfig.fortios.Name }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t("pages.wizard.steps.settings.fields.BackendConfig.fields.fortios.fields.Prefix.label")
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.fortios.Prefix }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.fortios.fields.VDOM.label",
                              ).replace(" (optional)", "")
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.fortios.VDOM || "-" }}
                          </p>
                        </div>
                      </template>
                      <template v-else-if="wizard.form.Vendor === 'fortimanager'">
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.fortimanager.fields.Hostname.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.fortimanager.Hostname }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.fortimanager.fields.Insecure.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.fortimanager.Insecure ? "true" : "false" }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.fortimanager.fields.ApiKey.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.fortimanager.ApiKey }}
                          </p>
                        </div>
                      </template>
                      <template v-else-if="wizard.form.Vendor === 'palo'">
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t("pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.Hostname.label")
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.panos.Hostname }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t("pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.Insecure.label")
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.panos.Insecure ? "true" : "false" }}
                          </p>
                        </div>
                        <div class="col-span-2">
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{ t("pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.ApiKey.label") }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.panos.ApiKey ? "provided" : "-" }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{ t("pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.VSys.label") }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.panos.VSys }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.panos.fields.DeviceGroup.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.panos.DeviceGroup }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{ t("pages.wizard.steps.settings.fields.ACLConfig.fields.panos.fields.GroupName.label") }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.ACLConfig.panos.GroupName }}
                          </p>
                        </div>
                      </template>
                      <template v-else-if="wizard.form.Vendor === 'firepower'">
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.firepower.fields.Hostname.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.firepower.Hostname }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.firepower.fields.Insecure.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.firepower.Insecure ? "true" : "false" }}
                          </p>
                        </div>
                        <div class="col-span-2">
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.firepower.fields.Username.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.firepower.Username }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t("pages.wizard.steps.settings.fields.ACLConfig.fields.firepower.fields.GroupUUID.label")
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.ACLConfig.firepower.GroupUUID }}
                          </p>
                        </div>
                      </template>
                      <template v-else-if="wizard.form.Vendor === 'sonicwall'">
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.sonicwall.fields.Hostname.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.sonicwall.Hostname }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.sonicwall.fields.Insecure.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.sonicwall.Insecure ? "true" : "false" }}
                          </p>
                        </div>
                        <div class="col-span-2">
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.sonicwall.fields.Username.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.sonicwall.Username }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t("pages.wizard.steps.settings.fields.ACLConfig.fields.sonicwall.fields.IPv4Group.label")
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.ACLConfig.sonicwall.IPv4Group }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t("pages.wizard.steps.settings.fields.ACLConfig.fields.sonicwall.fields.IPv6Group.label")
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.ACLConfig.sonicwall.IPv6Group }}
                          </p>
                        </div>
                      </template>
                      <template v-else-if="wizard.form.Vendor === 'sophosutm'">
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.sophosutm.fields.Hostname.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.sophosutm.Hostname }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.sophosutm.fields.Insecure.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.sophosutm.Insecure ? "true" : "false" }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.ACLConfig.fields.sophosutm.fields.GroupReference.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.ACLConfig.sophosutm.GroupReference }}
                          </p>
                        </div>
                      </template>
                      <template v-else-if="wizard.form.Vendor === 'sophossfos'">
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.sophossfos.fields.Hostname.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.sophossfos.Hostname }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.sophossfos.fields.Insecure.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.sophossfos.Insecure ? "true" : "false" }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.sophossfos.fields.Username.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.sophossfos.Username }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t("pages.wizard.steps.settings.fields.ACLConfig.fields.sophossfos.fields.GroupName.label")
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.ACLConfig.sophossfos.GroupName }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.ACLConfig.fields.sophossfos.fields.GroupNameV6.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.ACLConfig.sophossfos.GroupNameV6 }}
                          </p>
                        </div>
                      </template>
                      <template v-else-if="wizard.form.Vendor === 'azure'">
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t("pages.wizard.steps.settings.fields.BackendConfig.fields.azure.fields.ClientId.label")
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.azure.ClientId }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.azure.fields.ClientSecret.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.azure.ClientSecret }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t("pages.wizard.steps.settings.fields.BackendConfig.fields.azure.fields.TenantId.label")
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.azure.TenantId }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t("pages.wizard.steps.settings.fields.BackendConfig.fields.azure.fields.NoCache.label")
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.azure.NoCache ? "true" : "false" }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t("pages.wizard.steps.settings.fields.ACLConfig.fields.azure.fields.NsgDirection.label")
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.ACLConfig.azure.NsgDirection }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{ t("pages.wizard.steps.settings.fields.ACLConfig.fields.azure.fields.NsgName.label") }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.ACLConfig.azure.NsgName }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{ t("pages.wizard.steps.settings.fields.ACLConfig.fields.azure.fields.NsgPort.label") }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.ACLConfig.azure.NsgPort }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t("pages.wizard.steps.settings.fields.ACLConfig.fields.azure.fields.ResourceGroup.label")
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.ACLConfig.azure.ResourceGroup }}
                          </p>
                        </div>
                      </template>
                      <template v-else-if="wizard.form.Vendor === 'aws'">
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t("pages.wizard.steps.settings.fields.BackendConfig.fields.aws.fields.AccessKeyId.label")
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.aws.AccessKeyId }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.aws.fields.SecretAccessKey.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.aws.SecretAccessKey }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{ t("pages.wizard.steps.settings.fields.BackendConfig.fields.aws.fields.Region.label") }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.aws.Region }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t("pages.wizard.steps.settings.fields.ACLConfig.fields.aws.fields.SecurityGroupId.label")
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.ACLConfig.aws.SecurityGroupId }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{ t("pages.wizard.steps.settings.fields.ACLConfig.fields.aws.fields.Port.label") }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.ACLConfig.aws.Port }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{ t("pages.wizard.steps.settings.fields.ACLConfig.fields.aws.fields.Direction.label") }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.ACLConfig.aws.Direction }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{ t("pages.wizard.steps.settings.fields.ACLConfig.fields.aws.fields.Protocol.label") }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.ACLConfig.aws.Protocol }}
                          </p>
                        </div>
                      </template>
                      <template v-else-if="wizard.form.Vendor === 'cloudflarelist'">
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.ACLConfig.fields.cloudflarelist.fields.AccountId.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.cloudflarelist.AccountId }}
                          </p>
                        </div>
                        <div v-if="!!wizard.form.BackendConfig.cloudflarelist.ListName">
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.ACLConfig.fields.cloudflarelist.fields.ListName.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.cloudflarelist.ListName }}
                          </p>
                        </div>
                        <div v-else-if="!!wizard.form.BackendConfig.cloudflarelist.ListId">
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.ACLConfig.fields.cloudflarelist.fields.ListId.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.cloudflarelist.ListId }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.ACLConfig.fields.cloudflarelist.fields.ApiKey.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.cloudflarelist.ApiKey }}
                          </p>
                        </div>
                      </template>
                      <template v-else-if="wizard.form.Vendor === 'digitalocean'">
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.BackendConfig.fields.digitalocean.fields.PersonalAccessToken.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.BackendConfig.digitalocean.PersonalAccessToken }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.ACLConfig.fields.digitalocean.fields.FirewallID.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.ACLConfig.digitalocean.FirewallID }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.ACLConfig.fields.digitalocean.fields.Direction.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.ACLConfig.digitalocean.Direction }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t(
                                "pages.wizard.steps.settings.fields.ACLConfig.fields.digitalocean.fields.Protocol.label",
                              )
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.ACLConfig.digitalocean.Protocol }}
                          </p>
                        </div>
                        <div>
                          <h4 class="text-sm font-medium text-muted-foreground">
                            {{
                              t("pages.wizard.steps.settings.fields.ACLConfig.fields.digitalocean.fields.Ports.label")
                            }}
                          </h4>
                          <p class="truncate">
                            {{ wizard.form.ACLConfig.digitalocean.Ports }}
                          </p>
                        </div>
                      </template>
                    </template>
                    <div>
                      <h4 class="text-sm font-medium text-muted-foreground">
                        {{ t("pages.wizard.steps.settings.fields.Description.label") }}
                      </h4>
                      <p class="truncate">
                        {{ wizard.form.Description }}
                      </p>
                    </div>
                    <div>
                      <h4 class="text-sm font-medium text-muted-foreground">
                        {{ t("pages.wizard.steps.settings.fields.URL.label") }}
                      </h4>
                      <p class="truncate">
                        {{ wizard.form.URL || "-" }}
                      </p>
                    </div>
                  </div>
                </div>

                <div class="flex flex-col space-y-6 rounded-lg border bg-background p-6 shadow-sm transition-colors">
                  <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
                    {{ t("pages.wizard.steps.knocOptions.reviewTitle") }}
                  </h3>

                  <div class="grid w-full grid-cols-2 gap-4">
                    <div>
                      <h4 class="text-sm font-medium text-muted-foreground">
                        {{ t("pages.wizard.steps.settings.fields.MaximumGrantDuration.label") }}
                      </h4>
                      <p class="truncate">
                        <span v-if="wizard.knocOptionsForm.MaximumGrantDuration">
                          {{ wizard.knocOptionsForm.MaximumGrantDuration }}
                        </span>
                        <span v-else>-</span>
                      </p>
                    </div>
                    <div v-if="wizard.knocOptionsForm.GrantType.Type === 'userIp'">
                      <h4 class="text-sm font-medium text-muted-foreground">
                        {{ t("pages.wizard.steps.settings.fields.GrantType.heading") }}
                      </h4>
                      <p class="truncate">{{ t("pages.wizard.steps.settings.fields.GrantType.summary.usersIp") }}</p>
                    </div>
                  </div>

                  <div class="col-span-2 flex flex-col space-y-4">
                    <div v-if="wizard.knocOptionsForm.ClickToGrant" class="inline-flex items-center">
                      <span
                        :class="
                          twMerge(
                            'formkit-decorator relative block aspect-[1/1] w-[1em] select-none rounded border border-neutral-400 bg-white text-lg text-transparent shadow ring-blue-500 group-[]/multistep:shadow-none group-[]/repeater:shadow-none peer-focus-visible:ring-2 peer-focus-visible:ring-offset-1 dark:border-neutral-500 dark:bg-transparent dark:ring-offset-blue-500 dark:peer-focus-visible:ring-1 dark:peer-disabled:bg-neutral-600/50',
                            wizard.knocOptionsForm.ClickToGrant &&
                              'border-blue-600 bg-blue-50 text-blue-600 dark:bg-blue-900 dark:text-blue-400',
                          )
                        "
                      />
                      <span class="ml-2">
                        {{ t("pages.wizard.steps.settings.fields.ClickToGrant.label") }}
                      </span>
                    </div>
                    <div v-if="wizard.form.CGNATOpen" class="inline-flex items-center">
                      <span
                        :class="
                          twMerge(
                            'formkit-decorator relative block aspect-[1/1] w-[1em] select-none rounded border border-neutral-400 bg-white text-lg text-transparent shadow ring-blue-500 group-[]/multistep:shadow-none group-[]/repeater:shadow-none peer-focus-visible:ring-2 peer-focus-visible:ring-offset-1 dark:border-neutral-500 dark:bg-transparent dark:ring-offset-blue-500 dark:peer-focus-visible:ring-1 dark:peer-disabled:bg-neutral-600/50',
                            wizard.form.CGNATOpen &&
                              'border-blue-600 bg-blue-50 text-blue-600 dark:bg-blue-900 dark:text-blue-400',
                          )
                        "
                      />
                      <span class="ml-2">
                        {{ t("pages.wizard.steps.settings.fields.CGNATOpen.label") }}
                      </span>
                    </div>
                    <div v-if="wizard.knocOptionsForm.HideFromUsers" class="inline-flex items-center">
                      <span
                        :class="
                          twMerge(
                            'formkit-decorator relative block aspect-[1/1] w-[1em] select-none rounded border border-neutral-400 bg-white text-lg text-transparent shadow ring-blue-500 group-[]/multistep:shadow-none group-[]/repeater:shadow-none peer-focus-visible:ring-2 peer-focus-visible:ring-offset-1 dark:border-neutral-500 dark:bg-transparent dark:ring-offset-blue-500 dark:peer-focus-visible:ring-1 dark:peer-disabled:bg-neutral-600/50',
                            wizard.knocOptionsForm.HideFromUsers &&
                              'border-blue-600 bg-blue-50 text-blue-600 dark:bg-blue-900 dark:text-blue-400',
                          )
                        "
                      />
                      <span class="ml-2">
                        {{ t("pages.wizard.steps.settings.fields.HideFromUsers.label") }}
                      </span>
                    </div>
                    <div v-if="wizard.knocOptionsForm.AutoBrowseUsers" class="inline-flex items-center">
                      <span
                        :class="
                          twMerge(
                            'formkit-decorator relative block aspect-[1/1] w-[1em] select-none rounded border border-neutral-400 bg-white text-lg text-transparent shadow ring-blue-500 group-[]/multistep:shadow-none group-[]/repeater:shadow-none peer-focus-visible:ring-2 peer-focus-visible:ring-offset-1 dark:border-neutral-500 dark:bg-transparent dark:ring-offset-blue-500 dark:peer-focus-visible:ring-1 dark:peer-disabled:bg-neutral-600/50',
                            wizard.knocOptionsForm.AutoBrowseUsers &&
                              'border-blue-600 bg-blue-50 text-blue-600 dark:bg-blue-900 dark:text-blue-400',
                          )
                        "
                      />
                      <span class="ml-2">
                        {{ t("pages.wizard.steps.settings.fields.AutoBrowseUsers.label") }}
                      </span>
                    </div>
                  </div>

                  <div
                    v-if="wizard.knocOptionsForm.GrantType.Type !== 'userIp'"
                    class="col-span-2 flex flex-col space-y-4"
                  >
                    <h3 class="scroll-m-20 text-2xl font-semibold tracking-tight">
                      {{ t("pages.wizard.steps.settings.fields.GrantType.heading") }}
                    </h3>

                    <div class="col-span-2">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>
                              {{ t("pages.wizard.steps.settings.fields.GrantType.summary.table.heading1") }}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow v-for="{ Ip } in wizard.knocOptionsForm.GrantType.Ips" :key="Ip">
                            <TableCell>
                              {{ Ip }}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  <div class="w-full space-y-4">
                    <h4 class="scroll-m-20 text-xl font-semibold tracking-tight">
                      {{ t("pages.wizard.steps.settings.fields.IpRestrictions.heading") }}
                      <span v-if="wizard.knocOptionsForm.IpExclusions.RestrictionType !== 'none'"> </span>
                    </h4>
                    <h4 class="text-sm font-medium text-muted-foreground">
                      {{
                        (wizard.knocOptionsForm.IpExclusions.RfcType
                          ? t(
                              `pages.wizard.steps.settings.fields.IpRestrictions.rfc.options.${wizard.knocOptionsForm.IpExclusions.RfcType}`,
                            ) + " "
                          : "") +
                        t(
                          `pages.wizard.steps.settings.fields.IpRestrictions.options.${wizard.knocOptionsForm.IpExclusions.RestrictionType}`,
                        )
                      }}
                    </h4>

                    <div class="col-span-2" v-if="wizard.knocOptionsForm.IpExclusions.RestrictionType !== 'none'">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>
                              {{ t("pages.wizard.steps.settings.fields.IpRestrictions.fields.start.label") }}
                            </TableHead>
                            <TableHead>
                              {{ t("pages.wizard.steps.settings.fields.IpRestrictions.fields.end.label") }}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow v-for="{ Start, End } in wizard.knocOptionsForm.IpExclusions.Ranges" :key="Start">
                            <TableCell>
                              {{ Start }}
                            </TableCell>
                            <TableCell>
                              {{ End }}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>

                <div
                  v-if="wizard.form.ApplicationType !== 'active'"
                  class="flex flex-col space-y-6 rounded-lg border bg-background p-6 shadow-sm transition-colors"
                >
                  <h3 v-if="wizard.apiKeyOption === 'none'" class="scroll-m-20 text-2xl font-semibold tracking-tight">
                    {{ t("Allowlist access IP restriction") }}
                  </h3>
                  <h3 v-else class="scroll-m-20 text-2xl font-semibold tracking-tight">
                    {{ t("pages.wizard.steps.apiKey.heading") }}
                  </h3>

                  <div v-if="wizard.apiKeyOption === 'none'" class="grid w-full grid-cols-2 gap-4">
                    <div class="col-span-2">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>
                              {{ t("pages.wizard.steps.apiKey.fields.AllowedFrom.label") }}
                            </TableHead>
                            <TableHead>
                              {{ t("pages.wizard.steps.apiKey.fields.AllowedFrom.fields.note.label") }}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow v-if="wizard.apiKeyForm.AllowedFrom.length === 0">
                            <TableCell colspan="3" class="text-center text-muted-foreground">
                              {{ t("pages.wizard.steps.apiKey.fields.AllowedFrom.empty") }}
                            </TableCell>
                          </TableRow>
                          <TableRow
                            v-for="from in wizard.allowlistAccessIpRestrictionForm.AllowedFrom"
                            :key="from.prefix"
                          >
                            <TableCell>
                              {{ from.prefix }}
                            </TableCell>
                            <TableCell>
                              {{ from.note || "-" }}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                  <div v-else class="grid w-full grid-cols-2 gap-4">
                    <div>
                      <h4 class="text-sm font-medium text-muted-foreground">
                        {{ t("pages.wizard.steps.apiKey.fields.Name.label") }}
                      </h4>
                      <p class="truncate">
                        <template v-if="wizard.apiKeyOption === 'new'">
                          {{ wizard.apiKeyForm.Name }}
                        </template>
                        <template v-else>
                          {{ wizard.existingApiKey.Name }}
                        </template>
                      </p>
                    </div>

                    <div class="col-span-2">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>
                              {{ t("pages.wizard.steps.apiKey.fields.AllowedFrom.label") }}
                            </TableHead>
                            <TableHead>
                              {{ t("pages.wizard.steps.apiKey.fields.AllowedFrom.fields.note.label") }}
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody v-if="wizard.apiKeyOption === 'new'">
                          <TableRow v-if="wizard.apiKeyForm.AllowedFrom.length === 0">
                            <TableCell colspan="3" class="text-center text-muted-foreground">
                              {{ t("pages.wizard.steps.apiKey.fields.AllowedFrom.empty") }}
                            </TableCell>
                          </TableRow>
                          <TableRow v-for="from in wizard.apiKeyForm.AllowedFrom" :key="from.prefix">
                            <TableCell>
                              {{ from.prefix }}
                            </TableCell>
                            <TableCell>
                              {{ from.note || "-" }}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                        <TableBody v-else>
                          <TableRow v-if="wizard.existingApiKey.AllowedFrom.length === 0">
                            <TableCell colspan="3" class="text-center text-muted-foreground">
                              {{ t("pages.wizard.steps.apiKey.fields.AllowedFrom.empty") }}
                            </TableCell>
                          </TableRow>
                          <TableRow v-for="from in wizard.existingApiKey.AllowedFrom" :key="from.prefix">
                            <TableCell>
                              {{ from.prefix }}
                            </TableCell>
                            <TableCell>
                              {{ from.note || "-" }}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </div>

                <AgentsPreview
                  v-if="wizard.form.ApplicationType !== 'passive' && !!selectedAgent"
                  :agent="selectedAgent"
                  :get-warnings="getAgentWarnings"
                />
              </div>

              <div class="flex flex-col space-y-6">
                <AssignmentsPreview :users="selectedUsers" :groups="selectedGroups" />
              </div>
            </div>

            <div class="flex justify-end space-x-4">
              <button
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-muted-foreground underline underline-offset-4 ring-offset-background transition-colors hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                type="button"
                @click="wizard.changeStep(4)"
              >
                {{ t("common.back") }}
              </button>

              <button
                data-testid="submit-button"
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
                @click="handleSubmit"
              >
                {{ t("common.submit") }}
              </button>
            </div>
          </template>
        </template>
      </main>
    </div>
  </div>

  <Dialog v-if="Boolean(apiKey)" :show="Boolean(apiKey)">
    <DialogOverlay />

    <DialogPanel>
      <DialogTitle>
        {{ t("pages.wizard.steps.apiKey.dialogs.created.title") }}
      </DialogTitle>

      <DialogDescription class="flex flex-col">
        <template v-if="Boolean(acl)">
          {{ t("pages.wizard.steps.apiKey.dialogs.created.altDescription") }}
          <br />
          <br />

          1. Use a Basic Auth URL format:
          <CopySpan :display="authAclUrl" :value="authAclUrl" />

          <br />
          2. Or use a separate credentials format:
          <CopySpan :display="`URL: ${aclUrl}`" :value="aclUrl" />
          <CopySpan :display="`Username: apiuser`" value="apiuser" />
          <CopySpan :display="`Password (token): ${apiKey.PlaintextKey}`" :value="apiKey.PlaintextKey" />
        </template>
        <template v-else>
          <span>{{ t("pages.wizard.steps.apiKey.dialogs.created.description") }}</span>
          <CopySpan :display="`API Key: ${apiKey.PlaintextKey}`" :value="apiKey.PlaintextKey" />
        </template>
        <br />
        <span class="text-primary">{{ t("pages.wizard.steps.apiKey.dialogs.created.warning") }}</span>
      </DialogDescription>

      <button
        data-testid="close-button"
        type="button"
        class="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
        @click="
          wizard.$reset();
          router.push('/admin/knocs');
        "
      >
        {{ t("common.close") }}
      </button>
    </DialogPanel>
  </Dialog>

  <ErrorNotification
    :error="errorMessage"
    :title="t('common.error')"
    @update:error="(input) => (errorMessage = input)"
  />
</template>

<script setup>
import { DialogDescription } from "@headlessui/vue";
import { Bars3Icon } from "@heroicons/vue/24/solid";
import {
  ClipboardIcon,
  Cog8ToothIcon,
  CommandLineIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  KeyIcon,
  ServerIcon,
  TrashIcon,
  UsersIcon,
} from "@heroicons/vue/24/outline";
import { twMerge } from "tailwind-merge";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import globals from "../../globals";
import { createKnoc } from "../../utils";
import { isAgentCapableForApplication, getVersionWarnings } from "../../utils/agentCapabilities";

import { useWizardStore } from "../../pinia/wizard";

import SearchBar from "../../components/SearchBar.vue";
import SideBar from "../../components/SideBar.vue";
import ErrorNotification from "../../components/ErrorNotification.vue";
import CopySpan from "../../components/CopySpan.vue";
import AgentSelect from "../../components/AgentSelect.vue";
import UserSelect from "../../components/UserSelect.vue";
import GroupSelect from "../../components/GroupSelect.vue";
import KnocForm from "../../components/KnocForm.vue";
import KnocStepper from "../../components/KnocStepper.vue";
import AgentsPreview from "../../components/AgentsPreview.vue";
import AssignmentsPreview from "../../components/AssignmentsPreview.vue";

import { Dialog, DialogOverlay, DialogPanel, DialogTitle } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import KnocOptionsForm from "../../components/KnocOptionsForm.vue";
import ToggleButtons from "../../components/ui/formkit/ToggleButtons.vue";
import { createInput } from "@formkit/vue";
import AutoTable from "../../components/ui/table/AutoTable.vue";
import { DateTime } from "luxon";
import AdminHeader from "../../components/AdminHeader.vue";
import { useAdminStore } from "../../pinia/admin.ts";
import { useUserStore } from "../../pinia/user.ts";

const toggleButtons = createInput(ToggleButtons, {
  props: ["options"],
});

const { t } = useI18n();
const router = useRouter();

const adminStore = useAdminStore();
adminStore.fetchStatus();

const userStore = useUserStore();

const isLicensed = computed(() => adminStore.user?.Licensing?.Licensed);

const sidebarOpen = ref(false);
const search = ref("");
const errorMessage = ref(null);

const wizard = useWizardStore();

const autoComplete = import.meta.env?.VITE_AUTOCOMPLETE || "off";

//region apikeys
const apiKeys = ref([]);
const apiKeyEntryOptions = ["new", "existing", "none"];

const apiKeyTableCols = [
  { key: "Check", label: "checkbox", hidden: true },
  { key: "Name", label: t("fields.apiKeys.name"), sortable: true },
  { key: "AllowsAccessTo", label: t("fields.apiKeys.allowsAccessTo") },
  { key: "CreatedAt", label: t("fields.apiKeys.age"), sortable: true },
  { key: "ExpiredAfter", label: t("Expires"), sortable: true },
  { key: "AllowedFrom", label: t("fields.apiKeys.allowedFrom") },
];

const apiKeyTableRows = computed(() =>
  apiKeys.value.map((data) => ({
    Check: data,
    Name: data.Name,
    AllowsAccessTo: data.Resources.join(", "),
    CreatedAt: DateTime.fromISO(data.CreatedAt).toRelative(),
    ExpiredAfter: data.ExpiredAfter ? DateTime.fromISO(data.ExpiredAfter).toRelative() : t("Never"),
    AllowedFrom: data.AllowedFrom.map((e) => {
      let note = e.note ? ` (${e.note})` : "";
      return `${e.prefix}${note}`;
    }).join(", "),
    onClick: () => (wizard.existingApiKey = data),
    rowClass: "cursor-pointer",
  })),
);

//endregion

//region Agent
const agents = ref([]);

function isAgentCapable(agent) {
  return isAgentCapableForApplication(agent, wizard.form.ApplicationType, wizard.form.Vendor);
}

function getAgentWarnings(agent) {
  return getVersionWarnings(agent, isAgentCapable);
}

const selectedAgent = computed(() => {
  return agents.value.find((agent) => agent.ID === wizard.agentId);
});
//endregion

//region Users
const users = ref([]);
const selectedUsers = computed(() => {
  return users.value.filter((user) => wizard.userIds.includes(user.ID));
});
async function handleCreateUser([_, err]) {
  if (err) {
    errorMessage.value = globals.renderError(t, err);
    return;
  }

  fetchUsers();
}

const groups = ref([]);
const selectedGroups = computed(() => {
  return groups.value.filter((group) => wizard.groupIds.includes(group.ID));
});
//endregion

const apiKey = ref(null);
const acl = ref(null);
const aclUrl = computed(() => {
  if (!Boolean(acl.value)) {
    return "";
  }

  return `${window.location.protocol}//${window.location.hostname}${window.location.port ? `:${window.location.port}` : ""}/api/v1/allowlists/${acl.value.ID}/${acl.value.Config.allowlist.Token}.txt`;
});
const authAclUrl = computed(() => {
  if (!Boolean(acl.value) || !Boolean(apiKey.value.PlaintextKey)) {
    return "";
  }

  return `${window.location.protocol}//apiuser:${apiKey.value.PlaintextKey}@${window.location.hostname}${window.location.port ? `:${window.location.port}` : ""}/api/v1/allowlists/${acl.value.ID}/${acl.value.Config.allowlist.Token}.txt`;
});

async function handleSubmit() {
  try {
    const [knocId, apiKeyValue, aclValue] = await createKnoc({
      type: "APPLICATION",
      ...wizard,
      users: selectedUsers.value,
      groups: selectedGroups.value,
    });

    apiKey.value = apiKeyValue;
    acl.value = aclValue;

    if (!apiKey.value) {
      wizard.$reset();
      router.push({ path: "/admin/knocs", query: { viewKnocId: knocId } });
    }
  } catch (err) {
    errorMessage.value = globals.renderError(t, err);
  }
}

function fetchAgents() {
  userStore.fetchAgents({ force: true }).then(
    (response) => {
      agents.value = response.data;
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );
}

function fetchUsers() {
  userStore.fetchUsers({ force: true }).then(
    (response) => {
      users.value = response.data;
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );
}

function fetchGroups() {
  userStore.fetchGroups({ force: true }).then(
    (response) => {
      groups.value = response.data;
    },
    (error) => {
      error.value = globals.renderError(t, error);
    },
  );
}

function fetchApiKeys() {
  userStore.fetchApiKeys({ force: true }).then(
    (response) => {
      apiKeys.value = response.data;
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );
}

onMounted(() => {
  fetchAgents();
  fetchUsers();
  fetchGroups();
  fetchApiKeys();
});
</script>
